// Pagina di ritorno dopo un pagamento Stripe annullato.
// L'ordine resta PENDING; da qui si può rilanciare il pagamento dello STESSO ordine.
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";
import { apiFetch } from "../api/client";
import { paymentErrorKey } from "../api/paymentError";
import { pathFor } from "../app/paths";
import { analyticsIds } from "../analytics/tracker";

export function CheckoutCancel() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");

  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState("");

  const handleRetry = async () => {
    if (!orderId) return;
    setRetrying(true);
    setError("");
    try {
      const session = await apiFetch<{ checkout_url: string }>(
        "/api/v1/payments/stripe/checkout-session",
        { method: "POST", body: JSON.stringify({ order_id: orderId, ...analyticsIds() }) },
      );
      window.location.assign(session.checkout_url);
    } catch (err: unknown) {
      setError(t(paymentErrorKey(err)));
      setRetrying(false);
    }
  };

  return (
    <div style={{ minHeight: "calc(100vh - 200px)", background: cfg.color_light_bg, padding: "64px 24px" }}>
      <div
        style={{
          maxWidth: 520,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 16,
          padding: "56px 40px 40px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
          textAlign: "center",
        }}
      >
        <PausedMark color={cfg.color_accent} />

        <h1
          style={{
            fontFamily: cfg.font_heading,
            fontSize: 32,
            color: cfg.color_dark,
            margin: "28px 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.01em",
          }}
        >
          {t("checkout.cancel.title")}
        </h1>

        <p style={{ color: cfg.color_muted, fontSize: 15, lineHeight: 1.6, margin: "0 0 32px" }}>
          {t("checkout.cancel.body")}
        </p>

        {error && (
          <div
            style={{
              background: "#fff5f5",
              color: "#b91c1c",
              padding: "12px 16px",
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 20,
              textAlign: "left",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          {orderId && (
            <button
              onClick={handleRetry}
              disabled={retrying}
              style={{
                background: cfg.color_dark,
                color: "#fff",
                border: "none",
                padding: "14px 32px",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "0.02em",
                cursor: retrying ? "wait" : "pointer",
                opacity: retrying ? 0.7 : 1,
              }}
            >
              {retrying ? t("checkout.cancel.retrying") : t("checkout.cancel.retry")}
            </button>
          )}
          <Link
            to={`/${locale || "it"}/account`}
            style={{
              background: "transparent",
              color: cfg.color_dark,
              border: `1px solid ${cfg.color_dark}33`,
              padding: "14px 32px",
              borderRadius: 999,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            {t("checkout.cancel.myOrders")}
          </Link>
        </div>

        <div style={{ marginTop: 24 }}>
          <Link
            to={pathFor("collections", locale)}
            style={{ color: cfg.color_muted, fontSize: 13, textDecoration: "none" }}
          >
            ← {t("checkout.cancel.shop")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function PausedMark({ color }: { color: string }) {
  const size = 72;
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" style={{ display: "block", margin: "0 auto" }}>
      <circle cx="36" cy="36" r="34" fill={color} opacity="0.12" />
      <circle cx="36" cy="36" r="26" fill={color} />
      <rect x="29" y="25" width="4" height="22" rx="1.5" fill="#fff" />
      <rect x="39" y="25" width="4" height="22" rx="1.5" fill="#fff" />
    </svg>
  );
}
